import AudioFileOutlined from '@mui/icons-material/AudioFileOutlined'
import CodeOutlined from '@mui/icons-material/CodeOutlined'
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined'
import FolderZipOutlined from '@mui/icons-material/FolderZipOutlined'
import ImageOutlined from '@mui/icons-material/ImageOutlined'
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined'
import VideoFileOutlined from '@mui/icons-material/VideoFileOutlined'
import { SvgIconProps } from '@mui/material'

export interface FileTypeInfo {
  icon: SvgIconProps['component']
  extension: string
  label: string
}

// Helper function to get file extension from filename
const getExtension = (filename: string): string => {
  const extension = filename.split('.').pop()?.toUpperCase()
  return extension || 'FILE'
}

// Map MIME types to file groups
const getMimeTypeGroup = (mimeType: string): string => {
  const type = mimeType.toLowerCase()

  // Images
  if (type.startsWith('image/')) {
    return 'image'
  }

  // Videos
  if (type.startsWith('video/')) {
    return 'video'
  }

  // Audio
  if (type.startsWith('audio/')) {
    return 'audio'
  }

  // Code files
  if (
    type.startsWith('text/') &&
    [
      'text/javascript',
      'text/typescript',
      'text/html',
      'text/css',
      'text/xml',
      'text/yaml',
      'text/markdown',
    ].includes(type)
  ) {
    return 'code'
  }
  if (
    [
      'application/json',
      'application/xml',
      'application/javascript',
      'application/typescript',
    ].includes(type)
  ) {
    return 'code'
  }

  // Archives
  if (
    [
      'application/zip',
      'application/x-rar-compressed',
      'application/x-7z-compressed',
      'application/gzip',
      'application/x-tar',
    ].includes(type)
  ) {
    return 'archive'
  }

  // Documents - only for specific document types
  if (
    [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      'text/rtf',
    ].includes(type)
  ) {
    return 'document'
  }

  // Default fallback for unsupported types
  return 'default'
}

export const FILE_TYPE_ICONS = {
  document: DescriptionOutlined,
  image: ImageOutlined,
  video: VideoFileOutlined,
  audio: AudioFileOutlined,
  code: CodeOutlined,
  archive: FolderZipOutlined,
  default: InsertDriveFileOutlined,
}

export const getFileTypeInfo = (
  filename: string,
  mimeType: string
): FileTypeInfo => {
  const extension = getExtension(filename)
  const mimeGroup = getMimeTypeGroup(mimeType)
  const IconComponent =
    FILE_TYPE_ICONS[mimeGroup as keyof typeof FILE_TYPE_ICONS] ||
    FILE_TYPE_ICONS.default

  return {
    icon: IconComponent,
    extension,
    label: `${extension} File`,
  }
}
